import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { groupBy, maxBy, partition, sortBy } from 'lodash';
import { Item, Order, OrderStatusEvent } from '@baemingo/order-status-events/dist/types';
import { useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import Loader from '~ComponentsImproved/Loader';
import { Station } from '~Types';
import { stationMatchesItem } from '../utils';
import { combineKdsOrderItems } from './Order';
import OrderItem from './OrderItem';
import {
  KDSCompletedOrdersLog,
  KDSCompletedOrdersLogVariables,
} from './__queries__/KDSCompletedOrdersLog';
import {
  KDSCompletedOrdersLogEvents,
  KDSCompletedOrdersLogEventsVariables,
} from './__queries__/KDSCompletedOrdersLogEvents';
import CompletedOrdersLogWorker from '../../../Workers/CompletedOrdersLog?worker';
import {
  CompletedOrdersLogInput,
  CompletedOrdersLogResult,
} from '../../../Workers/CompletedOrdersLog';

/**
 * The universe as it was at the start of the business day, plus the events sent after it.
 * The backend folds events with sentAt <= $date into the snapshot and returns those with
 * sentAt > $date as events, so each event is expected in only one of the two.
 */
const COMPLETED_ORDERS_LOG_QUERY = gql`
  query KDSCompletedOrdersLog($venueId: ID!, $date: DateTime!, $limit: Int!) {
    venue: Venue(id: $venueId) {
      id
      eventOrderStatus {
        snapshot(date: $date)
        events(sentAfter: $date, limit: $limit) {
          items
          lastKey
        }
      }
    }
  }
`;

/** The events after the first page, the snapshot is only fetched once */
const COMPLETED_ORDERS_LOG_EVENTS_QUERY = gql`
  query KDSCompletedOrdersLogEvents(
    $venueId: ID!
    $date: DateTime!
    $limit: Int!
    $lastKey: Json!
  ) {
    venue: Venue(id: $venueId) {
      id
      eventOrderStatus {
        events(sentAfter: $date, limit: $limit, lastKey: $lastKey) {
          items
          lastKey
        }
      }
    }
  }
`;

/**
 * Events are fetched in pages, a whole busy day in one response can exceed the 6 MB
 * limit of a Lambda response
 */
const EVENTS_PAGE_SIZE = 2000;

const Section = styled.div`
  padding: 12px 20px;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterTab = styled.div<{ active: boolean }>`
  flex: 1;
  /* Long station names stay on one line and end with "…" instead of growing the tab */
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray500)')};
  background: ${({ active }) => (active ? 'var(--colorBlueGray1000)' : 'transparent')};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  color: var(--colorBlueGray200);
  padding: 10px 0;
  gap: 12px;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Time = styled.span`
  color: var(--colorBlueGray500);
  min-width: 44px;
`;

const Count = styled.div`
  margin-left: auto;
  min-width: 32px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  background: var(--colorBlueGray1000);
  border-radius: 6px;
  color: var(--colorWhite);
`;

const Items = styled.div`
  padding: 8px 0;
`;

const EmptyState = styled.div`
  color: var(--colorBlueGray500);
  font-size: 13px;
  padding: 8px 0;
`;

type CompletedOrder = {
  order: Order;
  items: Item[];
  completedAt: string;
};

/** Completed orders are listed for this many hours after their completion */
const COMPLETED_ORDERS_HOURS = 3;

/** The business day starts at 04:00 local time, like DEFAULT_TIMESTAMP in EventsSynchronizer */
export const getBusinessDayStart = () => {
  const now = DateTime.now();
  const start = now.startOf('day').plus({ hours: 4 });
  return (now < start ? start.minus({ days: 1 }) : start).toUTC().toISO()!;
};

interface Props {
  venueId: string;
  /** False while the log is mounted but hidden behind the summary tab */
  active: boolean;
  /**
   * Start of the replay, from getBusinessDayStart. It is also the key of the log, so a new
   * business day mounts a fresh log: new request, new worker, no replay of the previous day
   */
  businessDayStart: string;
}

/**
 * Read-only log of the orders completed in the KDS during the last COMPLETED_ORDERS_HOURS hours.
 * The whole business day is replayed in the CompletedOrdersLog worker (see there for how item
 * completion is detected), so items completed before that window still show up in the ticket.
 */
const CompletedOrdersLog: FC<Props> = ({ venueId, active, businessDayStart }) => {
  const { i18n } = useI18n();
  const logger = useLogger('kds-completed-orders-log');
  const [storage] = useStorage();
  const stations = useSelector((state) => state.stations || []);
  const liveEvents = useSelector((state) => state.orderStatusEvents);

  const selectedStation: Station | undefined = stations.find(
    (s: Station) => s.id === storage.selectedKDSStation,
  );

  const [filter, setFilter] = useState<'station' | 'venue'>(selectedStation ? 'station' : 'venue');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const changeFilter = (nextFilter: 'station' | 'venue') => {
    if (nextFilter === filter) {
      return;
    }

    setFilter(nextFilter);
    setExpandedOrderId(null);
  };

  const client = useApolloClient();

  // no-cache: VenueOrderStatus has no id, writing it to the cache would replace the
  // eventOrderStatus object the order status synchronizer reads
  const logQuery = useQuery<KDSCompletedOrdersLog, KDSCompletedOrdersLogVariables>(
    COMPLETED_ORDERS_LOG_QUERY,
    {
      variables: { venueId, date: businessDayStart, limit: EVENTS_PAGE_SIZE },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        // Venue is nullable, and without it there is nothing to replay
        if (!data.venue?.eventOrderStatus) {
          logger.error('Completed orders log data has no venue order status', { venueId });
        }
      },
      onError: (e) => {
        logger.error('Failed to fetch completed orders log data', {
          venueId,
          businessDayStart,
          errorMessage: e.message,
        });
      },
    },
  );

  // Events of the pages after the first one, null until all of them are loaded
  const [nextPagesEvents, setNextPagesEvents] = useState<OrderStatusEvent[] | null>(null);
  const [nextPagesFailed, setNextPagesFailed] = useState(false);

  /** Loads the remaining pages one after another, the replay waits until all of them are there */
  useEffect(() => {
    const firstPage = logQuery.data?.venue?.eventOrderStatus?.events;

    if (!firstPage) {
      return;
    }

    // Pages of an earlier first page must not be combined with this one
    setNextPagesEvents(null);
    setNextPagesFailed(false);

    // Set on unmount or a new first page, so the results of a load that is still running are dropped
    let cancelled = false;

    (async () => {
      const events: OrderStatusEvent[] = [];
      let lastKey = firstPage.lastKey;

      try {
        while (lastKey) {
          const page = await client.query<
            KDSCompletedOrdersLogEvents,
            KDSCompletedOrdersLogEventsVariables
          >({
            query: COMPLETED_ORDERS_LOG_EVENTS_QUERY,
            variables: { venueId, date: businessDayStart, limit: EVENTS_PAGE_SIZE, lastKey },
            fetchPolicy: 'no-cache',
          });

          if (cancelled) {
            return;
          }

          const pageEvents = page.data.venue?.eventOrderStatus.events;

          if (!pageEvents) {
            throw new Error('Events page has no venue order status');
          }

          events.push(...pageEvents.items);
          lastKey = pageEvents.lastKey;
        }
      } catch (e) {
        if (!cancelled) {
          logger.error('Failed to fetch completed orders log events page', {
            venueId,
            businessDayStart,
            loadedEvents: events.length,
            errorMessage: (e as Error)?.message,
          });
          setNextPagesFailed(true);
        }
        return;
      }

      if (!cancelled) {
        setNextPagesEvents(events);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logQuery.data]);

  // Replay result of the latest run, and the id of that run. Results of older runs are ignored
  const [replay, setReplay] = useState<CompletedOrdersLogResult | null>(null);
  // Set when the worker failed to load, threw, or a message could not be passed. There are no retries,
  // the log shows an error until it mounts again (the summary panel is reopened or the page reloads)
  const [workerFailed, setWorkerFailed] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const workerRunId = useRef(0);

  // At most one replay runs at a time. Input that arrives meanwhile waits in `pending`, where newer
  // input replaces older, so the worker never works through a queue of outdated replays
  const busy = useRef(false);
  const pending = useRef<CompletedOrdersLogInput | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  const onWorkerFailed = (worker: Worker | null, type: string, errorMessage?: string) => {
    // A callback of a worker from an earlier mount must not stop or fail the current one
    if (worker !== workerRef.current) {
      return;
    }

    logger.error('Completed orders log worker failed', { type, errorMessage });

    worker?.terminate();
    workerRef.current = null;

    busy.current = false;
    pending.current = null;
    setWorkerFailed(true);
  };

  const sendToWorker = (input: CompletedOrdersLogInput) => {
    const worker = workerRef.current;

    if (!worker) {
      onWorkerFailed(null, 'missing');
      return;
    }

    busy.current = true;
    pending.current = null;

    try {
      worker.postMessage(input);
    } catch (e) {
      // postMessage throws synchronously when the input cannot be cloned
      onWorkerFailed(worker, 'postMessage', (e as Error)?.message);
    }
  };

  useEffect(() => {
    const worker = new CompletedOrdersLogWorker();

    worker.onmessage = (e: MessageEvent<CompletedOrdersLogResult>) => {
      busy.current = false;

      // A hidden log replays again with fresh input once it is shown, so the pending input can wait
      if (pending.current && activeRef.current) {
        sendToWorker(pending.current);
      }

      // Pending or newer input got a higher run id, so this result is outdated
      if (e.data.id !== workerRunId.current) {
        return;
      }

      e.data.failedEvents.forEach((failedEvent) => {
        logger.warn('Failed to replay order status event', failedEvent);
      });

      setReplay(e.data);
    };

    // Fired when the worker script fails to load or the replay throws outside its try/catch,
    // in both cases no result for the run in flight will arrive
    worker.onerror = (e: ErrorEvent) => onWorkerFailed(worker, 'error', e.message);
    // Fired when a result cannot be deserialized, the run in flight is lost as well
    worker.onmessageerror = () => onWorkerFailed(worker, 'messageerror');

    workerRef.current = worker;

    return () => {
      worker.terminate();

      if (workerRef.current === worker) {
        workerRef.current = null;
      }
    };
  }, []);

  /**
   * Replay the day again whenever the fetched data or the live events change while the log is shown.
   * While hidden nothing is replayed, showing it again replays with the current live events.
   */
  useEffect(() => {
    const eventOrderStatus = logQuery.data?.venue?.eventOrderStatus;

    if (!eventOrderStatus || !nextPagesEvents || !active || workerFailed) {
      return;
    }

    const fetchedEvents: OrderStatusEvent[] = [
      ...eventOrderStatus.events.items,
      ...nextPagesEvents,
    ];

    // Events synced after the fetch (or not sent yet). Events sent before the day start are already in
    // the snapshot, and events also in the fetched set are dropped by eventId in the worker
    const newLiveEvents = liveEvents.filter(
      (event) => event.sentAt === null || event.sentAt > businessDayStart,
    );

    workerRunId.current++;

    const input: CompletedOrdersLogInput = {
      id: workerRunId.current,
      snapshot: eventOrderStatus.snapshot,
      events: [...fetchedEvents, ...newLiveEvents],
    };

    if (busy.current) {
      pending.current = input;
    } else {
      sendToWorker(input);
    }
  }, [logQuery.data, nextPagesEvents, liveEvents, businessDayStart, active, workerFailed]);

  /**
   * Display filter only: lists the completed orders that contain items for the selected station,
   * with the same station rules as useFilterOrders (without the order status filter)
   */
  const matchesFilter = (order: Order, items: Item[]) => {
    if (filter === 'venue' || !selectedStation) {
      return true;
    }

    const itemMatch =
      items.some((item) => stationMatchesItem(selectedStation, item)) ||
      (!!order.realId && !storage.enableNewKDSAppOrders);
    const serviceTypeMatch =
      !order.serviceType ||
      selectedStation.serviceTypes.length === 0 ||
      selectedStation.serviceTypes.includes(order.serviceType);
    const originMatch =
      !order.origin ||
      selectedStation.origins.length === 0 ||
      selectedStation.origins.includes(order.origin);

    return itemMatch && serviceTypeMatch && originMatch;
  };

  /**
   * An order is completed once none of its items is left in the universe, regardless of station.
   * It completed when its last item did, and is listed if that was within COMPLETED_ORDERS_HOURS.
   */
  const completedOrders = useMemo(() => {
    if (!replay) {
      return [];
    }

    // Evaluated on every replay and whenever the log is shown again, so the window stays current
    const windowStart = DateTime.now()
      .minus({ hours: COMPLETED_ORDERS_HOURS })
      .toUTC()
      .toISO()!;
    const inProgressOrderIds = new Set(replay.remaining.map((item) => item.orderId));
    const byOrder = groupBy(replay.completed, (entry) => entry.order.id);

    const orders = Object.entries(byOrder).reduce((arr, [orderId, entries]) => {
      if (inProgressOrderIds.has(orderId)) {
        return arr;
      }

      const { completedAt, order } = maxBy(entries, 'completedAt')!;
      const items = entries.map((entry) => entry.item);

      if (completedAt >= windowStart && matchesFilter(order, items)) {
        arr.push({ order, items, completedAt });
      }

      return arr;
    }, [] as CompletedOrder[]);

    return sortBy(orders, 'completedAt').reverse();
  }, [replay, active, filter, selectedStation, storage.enableNewKDSAppOrders]);

  /** Stack identical items like the KDS order does, upsell children are listed one by one */
  const renderItems = (items: Item[]) => {
    const [stackableItems, rest] = partition(items, (item) => !item.parentId);

    return [
      ...combineKdsOrderItems(stackableItems),
      ...rest.map((item) => ({ ...item, count: 1 })),
    ].map((item) => (
      <OrderItem
        key={item.id}
        item={item}
        count={item.count}
        isChild={!!item.parentId && item.parentId !== item.id}
        isBuffering={false}
        disableItemProgression
      />
    ));
  };

  // The request failed, it returned no venue order status, or the replay failed. The last replay
  // result is not shown then, it may be outdated
  const failed =
    !!logQuery.error ||
    (!!logQuery.data && !logQuery.data.venue?.eventOrderStatus) ||
    nextPagesFailed ||
    workerFailed;

  return (
    <Section>
      {selectedStation && (
        <FilterTabs>
          <FilterTab active={filter === 'station'} onClick={() => changeFilter('station')}>
            {selectedStation.name}
          </FilterTab>
          <FilterTab active={filter === 'venue'} onClick={() => changeFilter('venue')}>
            {i18n('KDS.Logs.AllStations')}
          </FilterTab>
        </FilterTabs>
      )}

      {!replay && !failed && <Loader size="sm" color="rgba(255,255,255,0.7)" />}

      {failed && <EmptyState>{i18n('KDS.ERROR')}</EmptyState>}

      {!failed && replay && completedOrders.length === 0 && (
        <EmptyState>{i18n('KDS.Logs.Empty')}</EmptyState>
      )}

      {!failed &&
        completedOrders.map(({ order, items, completedAt }) => (
          <div key={order.id}>
            <Row
              onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}>
              <Time>{DateTime.fromISO(completedAt).toFormat('HH:mm')}</Time>
              <span>{order.tableName || order.easyId}</span>
              <Count>{items.length}</Count>
            </Row>
            {expandedOrderId === order.id && <Items>{renderItems(items)}</Items>}
          </div>
        ))}
    </Section>
  );
};

export default CompletedOrdersLog;
